// @ts-ignore esModuleInterop
import React from 'react'
// @ts-ignore no types
import { connect } from 'react-redux'
import classnames from 'classnames/bind'
import type {
  primitives,
  AuctionBlueprintOnlineReactiveArgs,
} from '@adc/blueprint'
// @ts-ignore no types
import * as blueprint from '@adc/blueprint'
import * as graph from '@adc/sdk'
import type { FeatureFlags } from '@adc/ui-adc'
import { usePropertyContext } from 'common/context/property'
import { useListingContext } from 'common/context/listing'
import { shouldShowSealedBidTreatment } from 'common/utils/privateSeller'
import { shouldShowBanner } from '../../components/bidBanner'
import { showTPSOnlineBidBanner } from '../tpsOnlineBidBanner'
import { fetchUser } from '../../actions/user'
import { withModalHooks } from '../../hooks/useModalFeatures'
import {
  Before,
  During,
  After,
  ComingSoon,
  Offline,
  TPS as Tps,
  Postponed,
  AfterTPS,
  Pending,
} from './states'
import { AdditionalAuctionDetails } from './additionalAuctionDetails'
// @ts-ignore no types
import styles from './index.scss'
import { AuctionDetailsPropTypes } from './common'
import { isPostPoned, isReAuctionPending } from './utils'

export { AuctionDetailsPropTypes }

const cx = classnames.bind(styles)

/* eslint-enable react/no-unused-prop-types */

interface AuctionDetailsProps {
  segment: AuctionBlueprintOnlineReactiveArgs['segment']
  listing: AuctionBlueprintOnlineReactiveArgs['listing']
  isSaved: boolean
  featureFlags: Record<string, any>
  similarProperties: any[]
  isInTour: boolean
  actions: any
  pushAuthQueueItem: (items: any) => void
  toggleAuthModal: () => void
  displayInline?: boolean
  user: primitives.AuctionUser
  isRedesignEnabled: boolean
  isFirstLookEnabled: boolean
  onClickAiPrompt: (prompt?: string) => void
}

const AuctionDetails = (props: AuctionDetailsProps) => {
  const { segment, listing } = useListingContext()
  const { property } = usePropertyContext()

  const { featureFlags, displayInline } = props
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const isRedesignEnabled: boolean = featureFlags['app.enable-pdp-design-2025']
  // merging context segment and listing into props to ensure child components get the updated data until they can be refactored to look directly at context
  props = {
    ...props,
    segment,
    listing,
    isSaved: Boolean(property.is_currently_saved),
    isRedesignEnabled,
    // @ts-ignore wrong typing
    isFirstLookEnabled: listing?.listing_configuration?.is_first_look_enabled,
  }

  const isForeClosureOnlineAuction =
    listing.listing_configuration?.asset_type ===
      graph.enums.AssetType.Foreclosure &&
    segment?.__typename === 'OnlineAuctionSegment'
  const isNonForeclosureEnglishOnlineAuctionAndBannerIsShowing =
    shouldShowBanner(segment, listing) && !isForeClosureOnlineAuction

  // this is very messy and should be refactored, but for now, we are showing the bid box for sealed bid specifically to allow the
  // sealed bid registed and make offer flows (which are triggered from the bid box) until the rest of PDP is shifted around to handle this better
  const hideBidBox =
    (segment?.segment_type !== graph.enums.OnlineAuctionSegmentType.SealedBid ||
      !featureFlags['fe.resi.enable-private-seller']) &&
    (isNonForeclosureEnglishOnlineAuctionAndBannerIsShowing ||
      (isForeClosureOnlineAuction &&
        showTPSOnlineBidBanner(segment, listing, featureFlags)))
  let auctionDetails = null

  // we need to allow CTAs for already registered users in sealed bid auctions to reach the offer management screen
  // and for them the auction is still in progress so we add these additional statuses for during for sealed bid
  // there will be later checks stopping un registered users from clicking those CTAs in the sealed bid components
  const auctionStatus = segment?.__methods__?.getAuctionStatus?.(listing)

  const englishAuctionShowDuring = auctionStatus === 'Live'

  const sealedBidAuctionShowDuring =
    auctionStatus === 'Live' ||
    (segment?.segment_status &&
      [
        graph.enums.OnlineAuctionSegmentStatus.HighestAndBestDecision,
        graph.enums.OnlineAuctionSegmentStatus.HighestAndBestOpen,
      ].includes(segment?.segment_status))

  const showDuring = shouldShowSealedBidTreatment({ segment, featureFlags })
    ? sealedBidAuctionShowDuring
    : englishAuctionShowDuring

  if (!hideBidBox) {
    if (
      blueprint.listing.utils.isFclOnlineAuction(listing) ||
      !blueprint.listing.utils.isTrustee(listing)
    ) {
      if (!listing.auction?.is_online) {
        // was bStatus.isLiveEvent
        auctionDetails = (
          <Offline
            user={props.user}
            isRedesignEnabled={props.isRedesignEnabled}
            featureFlags={props.featureFlags}
          />
        )
      } else if (isPostPoned(listing)) {
        // REO doesn't have Postponed status. This should only apply for TPS Online
        auctionDetails = (
          <Postponed
            similarProperties={props.similarProperties}
            isInTour={props.isInTour}
            isRedesignEnabled={props.isRedesignEnabled}
          />
        )
      } else if (showDuring) {
        auctionDetails = (
          <During
            isInTour={props.isInTour}
            featureFlags={props.featureFlags}
            isFirstLookEnabled={props.isFirstLookEnabled}
            user={props.user}
            isRedesignEnabled={props.isRedesignEnabled}
          />
        )
      } else if (listing.is_coming_soon) {
        auctionDetails = (
          <ComingSoon
            pushAuthQueueItem={props.pushAuthQueueItem}
            toggleAuthModal={props.toggleAuthModal}
          />
        )
      } else if (
        // this was
        // bStatus.isAuctionPending &&
        // !bStatus.isStatusMissing
        segment?.segment_status ===
        graph.enums.OnlineAuctionSegmentStatus.Pending
      ) {
        auctionDetails = (
          <Before
            isInTour={props.isInTour}
            similarProperties={props.similarProperties}
            featureFlags={props.featureFlags}
            isFirstLookEnabled={props.isFirstLookEnabled}
            user={props.user}
            isRedesignEnabled={props.isRedesignEnabled}
          />
        )
      } else if (isReAuctionPending(listing)) {
        auctionDetails = (
          <Pending
            similarProperties={props.similarProperties}
            isInTour={props.isInTour}
            featureFlags={props.featureFlags}
            isRedesignEnabled={props.isRedesignEnabled}
          />
        )
      }
      // This is a special case we are covering in the case that starting bid was not populated in the listing creation when a listing is online (should not be valid scenario, but we've found listings that landed this condition).
      // This set of conditions is preventing the segment from being created. As this needs to be filled at some point of the listing's lifecycle we are considering the status as Coming Soon.
      else if (!listing?.auction?.starting_bid && listing.auction?.is_online) {
        auctionDetails = (
          <ComingSoon
            pushAuthQueueItem={props.pushAuthQueueItem}
            toggleAuthModal={props.toggleAuthModal}
          />
        )
      } else {
        if (blueprint.listing.utils.isFclOnlineAuction(listing)) {
          auctionDetails = (
            <AfterTPS
              similarProperties={props.similarProperties}
              isRedesignEnabled={props.isRedesignEnabled}
            />
          )
        } else {
          auctionDetails = (
            <After
              similarProperties={props.similarProperties}
              pushAuthQueueItem={props.pushAuthQueueItem}
              toggleAuthModal={props.toggleAuthModal}
              featureFlags={props.featureFlags}
              user={props.user}
              isRedesignEnabled={props.isRedesignEnabled}
            />
          )
        }
      }
    } else {
      auctionDetails = <Tps {...props} />
    }
  }

  return (
    <section className={cx('container')}>
      <div className={cx({ 'right-rail': !displayInline })}>
        {auctionDetails && (
          <div data-elm-id='auction_details' className='u-mb-3'>
            {auctionDetails}
          </div>
        )}
        <AdditionalAuctionDetails listing={listing} />
      </div>
    </section>
  )
}

const mapStateToProps = (store: any) => ({
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  featureFlags: store.featureFlags as FeatureFlags,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  isInTour: store.tutorial.tutorial > 0,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
  similarProperties: store.propertyAnalytics?.similarProperties,
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
  user: store.user as primitives.AuctionUser,
})

const mapDispatchToProps = (dispatch: any) => ({
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-call
  fetchUser: () => dispatch(fetchUser()),
})

const mergeProps = (
  storeProps: any,
  actions: any,
  ownProps: AuctionDetailsProps
) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return {
    ...ownProps,
    ...storeProps,
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    actions,
  }
}

export default connect<
  ReturnType<typeof mapStateToProps>,
  ReturnType<typeof mapDispatchToProps>,
  ReturnType<typeof mergeProps>
>(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps
)(withModalHooks(AuctionDetails))
